/**
 * Offertspår i gränssnittet: originalspåret och tilläggsofferter.
 *
 * En "tilläggsoffert" är ett tillägg under produktion som kunden godkänner VID
 * SIDAN AV den redan godkända originalofferten. Spåret bor i `extraNummer` på
 * kalkylen (som äger valet), PU-tråden och offerten:
 *
 *   null  → originalspåret (allt som fanns före den här funktionen)
 *   1/2/3 → Tilläggsoffert 1/2/3
 *
 * Varje spår har sin EGEN versionskedja. Därför måste all jämförelse av
 * versioner — "finns en nyare?", "matchar offerten kalkylen?", "är den här
 * ersatt?" — hålla sig inom ett spår. Tilläggsoffert 1 börjar på v1.0 även när
 * originalspåret står på v1.5, och de två talen får aldrig jämföras med
 * varandra.
 *
 * Filen finns för att den kunskapen ska bo på ETT ställe i frontend, precis som
 * `backend/src/utils/offertSpar.js` gör på serversidan. Sprids
 * `if (o.extraNummer)` ut över KalkylPage, MyProjects, OffertCard,
 * versionHelpers och pipelinecellerna hamnar vi i samma läge som IA-arvodet
 * gjorde i oktober 2026: flera källor till samma sanning, som glider isär.
 */

/** Är dokumentet ett tillägg (tilläggsoffert) och inte originalspåret? */
export const arExtra = (doc) => doc?.extraNummer != null;

/** Hör dokumentet till originalspåret? Sant för allt som saknar fältet. */
export const arOriginal = (doc) => doc?.extraNummer == null;

/** Spårets nummer, normaliserat: null för originalspåret. */
export const sparAv = (doc) => doc?.extraNummer ?? null;

/** Ligger de två dokumenten i samma spår? */
export const sammaSpar = (a, b) => sparAv(a) === sparAv(b);

/**
 * Spårets namn utan version — "Tilläggsoffert 1", eller null för originalspåret.
 * Används i rubriker, banners och felmeddelanden där versionen är i vägen.
 */
export const sparNamn = (doc) => (arExtra(doc) ? `Tilläggsoffert ${doc.extraNummer}` : null);

/**
 * Beteckningen: "v1.5", "Tilläggsoffert 1 v1.0", "Tilläggsoffert 1 v1.1".
 *
 * Versionen står ALLTID med, även på v1.0. Spåret och versionen svarar på två
 * olika frågor — vilket tillägg, och vilken revidering av det — och får man
 * feedback på ett skickat tillägg och gör om det ska det gå att se att v1.1
 * fortfarande är samma Tilläggsoffert 1. Utan versionen blev "Tilläggsoffert 1"
 * och dess revidering omöjliga att skilja åt i versionshistoriken.
 *
 * Backend skickar med samma sträng som `offert.beteckning` där den finns —
 * använd den först, och den här som fallback för svar som inte bär fältet.
 *
 * VISNINGSSTRÄNG. Får aldrig jämföras, sorteras eller parseFloat:as; `version`
 * är kvar som det numeriska värdet inom spåret.
 */
export const beteckning = (doc) => {
  const v = doc?.version || '1.0';
  if (!arExtra(doc)) return `v${v}`;
  return `${sparNamn(doc)} v${v}`;
};

/** Beteckningen, men tar serverns beräknade värde när det finns. */
export const visaBeteckning = (doc) => doc?.beteckning || beteckning(doc);
